import React, { useMemo, useState } from "react"
import classnames from "classnames"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap"
import { toast } from "react-toastify"
import PageWrapper from "../../../components/Common/PageWrapper"
import TableContainer from "../../../components/Common/TableContainer"
import { useListParams } from "../../../hooks/useListParams"
import { useQueueJobs } from "../../../hooks/useQueueJobs"

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
]

const STATUS_BADGES = {
  pending: "warning",
  processing: "info",
  failed: "danger",
}

const TABS = [
  { id: "monitor", label: "Queue Monitor", icon: "mdi-format-list-bulleted" },
  { id: "cron", label: "Cron Setup", icon: "mdi-clock-outline" },
]

const formatDateTime = value => {
  if (!value) return "N/A"

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value))
}

const compactClassName = value => {
  if (!value) return "N/A"

  const parts = String(value).split("\\")
  return parts[parts.length - 1] || value
}

const cronCommands = endpoint => [
  {
    type: "wget",
    command: `wget -q -O - "${endpoint}" >/dev/null 2>&1`,
  },
  {
    type: "curl",
    command: `curl -s "${endpoint}" >/dev/null 2>&1`,
  },
]

const JobList = () => {
  document.title = "Queue Jobs - Aurobil"

  const [activeTab, setActiveTab] = useState("monitor")
  const { params, setPage, setSearch, resetParams, updateParams } = useListParams({
    page: 1,
    search: "",
    status: "all",
    queue: "",
  })

  const queryParams = useMemo(
    () => ({
      page: params.page || 1,
      per_page: params.per_page || 50,
      search: params.search,
      status: params.status || "all",
      queue: params.queue,
    }),
    [params]
  )

  const { data, isLoading, isRefetching, refetch } = useQueueJobs(queryParams)
  const jobs = data?.data?.jobs?.data || []
  const paginationInfo = data?.data?.jobs
  const summary = data?.data?.summary || { pending: 0, failed: 0 }
  const cronJobs = data?.data?.cron_jobs || []
  const allCronConfigured = cronJobs.length > 0 && cronJobs.every(item => item.configured)

  const handleStatusChange = value => {
    updateParams(prev => ({ ...prev, status: value, page: 1 }))
  }

  const handleQueueChange = value => {
    updateParams(prev => ({ ...prev, queue: value, page: 1 }))
  }

  const copyCommand = async command => {
    try {
      await navigator.clipboard.writeText(command)
      toast.success("Cron command copied.")
    } catch {
      toast.error("Copy failed. Please select and copy the command manually.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <span className="font-monospace fw-semibold">#{row.original.id}</span>
        ),
      },
      {
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.status
          return (
            <Badge color={STATUS_BADGES[status] || "secondary"} className="text-capitalize">
              {status}
            </Badge>
          )
        },
      },
      {
        header: "Queue",
        accessorKey: "queue",
        cell: ({ row }) => <span className="fw-semibold">{row.original.queue}</span>,
      },
      {
        header: "Job",
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{compactClassName(row.original.display_name)}</div>
            <div className="small text-muted">{row.original.job_class || "N/A"}</div>
          </div>
        ),
      },
      {
        header: "UUID",
        cell: ({ row }) => (
          <span className="font-monospace small">{row.original.uuid || "N/A"}</span>
        ),
      },
      {
        header: "Attempts",
        cell: ({ row }) => row.original.attempts ?? "N/A",
      },
      {
        header: "Available",
        cell: ({ row }) => (
          <small className="text-muted">{formatDateTime(row.original.available_at)}</small>
        ),
      },
      {
        header: "Reserved",
        cell: ({ row }) => (
          <small className="text-muted">{formatDateTime(row.original.reserved_at)}</small>
        ),
      },
      {
        header: "Failed",
        cell: ({ row }) => (
          <small className="text-muted">{formatDateTime(row.original.failed_at)}</small>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Settings" breadcrumbItem="Jobs">
      <Card>
        <CardBody className="pb-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h4 className="card-title mb-1">Jobs</h4>
              <p className="text-muted mb-0">
                Queue monitoring and shared-hosting cron setup.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Badge color="warning" className="px-3 py-2">
                Pending: {summary.pending}
              </Badge>
              <Badge color="danger" className="px-3 py-2">
                Failed: {summary.failed}
              </Badge>
              <Badge color={allCronConfigured ? "success" : "warning"} className="px-3 py-2">
                {allCronConfigured ? "Runner Key Ready" : "Runner Key Missing"}
              </Badge>
            </div>
          </div>

          <Nav tabs className="nav-tabs-custom">
            {TABS.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  href="#"
                  className={classnames({ active: activeTab === tab.id })}
                  onClick={event => {
                    event.preventDefault()
                    setActiveTab(tab.id)
                  }}
                >
                  <i className={`mdi ${tab.icon} me-1`} />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </CardBody>
      </Card>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="monitor">
          <Card>
            <CardBody>
              <Row className="g-3 align-items-end mb-4">
                <Col xs={12} md={3}>
                  <Label className="mb-1 small fw-semibold">Status</Label>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={params.status || "all"}
                    onChange={e => handleStatusChange(e.target.value)}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col xs={12} md={3}>
                  <Label className="mb-1 small fw-semibold">Queue</Label>
                  <Input
                    bsSize="sm"
                    placeholder="default, notifications"
                    value={params.queue || ""}
                    onChange={e => handleQueueChange(e.target.value)}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Label className="mb-1 small fw-semibold">Search</Label>
                  <Input
                    bsSize="sm"
                    placeholder="Search job, queue, UUID"
                    value={params.search || ""}
                    onChange={e => setSearch(e.target.value)}
                  />
                </Col>
                <Col xs="auto">
                  <Button color="secondary" size="sm" outline onClick={() => resetParams()}>
                    <i className="mdi mdi-refresh me-1" />
                    Reset
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button color="primary" size="sm" outline onClick={() => refetch()} disabled={isRefetching}>
                    <i className="mdi mdi-reload me-1" />
                    Refresh
                  </Button>
                </Col>
              </Row>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner size="sm" color="primary" />
                  <p className="text-muted small mt-2">Loading queue jobs...</p>
                </div>
              ) : (
                <>
                  <TableContainer
                    columns={columns}
                    data={jobs}
                    isPagination={!!paginationInfo}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isRefetching}
                  />

                  <div className="small text-muted mt-3">
                    Showing {jobs.length} of {paginationInfo?.total || 0} jobs
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </TabPane>

        <TabPane tabId="cron">
          <Card>
            <CardBody>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner size="sm" color="primary" />
                  <p className="text-muted small mt-2">Loading cron setup...</p>
                </div>
              ) : (
                <>
                  <Row className="g-3 mb-4">
                    <Col xs={12} md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="small text-muted mb-1">Secret Key</div>
                        <div className="fw-semibold">
                          {allCronConfigured ? "Configured" : "Not Configured"}
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="small text-muted mb-1">Runner Endpoint</div>
                        <div className="fw-semibold">HTTP Cron</div>
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div className="border rounded p-3 h-100">
                        <div className="small text-muted mb-1">Hosting Mode</div>
                        <div className="fw-semibold">cPanel / Shared Hosting</div>
                      </div>
                    </Col>
                  </Row>

                  {!allCronConfigured && (
                    <div className="alert alert-warning">
                      Set <span className="font-monospace">QUEUE_RUNNER_KEY</span> in Laravel
                      <span className="font-monospace"> .env</span>, then reload this page.
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Job</th>
                          <th>Frequency</th>
                          <th>Command</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronJobs.map(item => (
                          <tr key={item.key}>
                            <td style={{ minWidth: 240 }}>
                              <div className="fw-semibold">{item.name}</div>
                              <div className="small text-muted">{item.description}</div>
                            </td>
                            <td>
                              <Badge color="light" className="text-dark font-monospace">
                                {item.frequency}
                              </Badge>
                            </td>
                            <td style={{ minWidth: 520 }}>
                              {cronCommands(item.endpoint).map(commandItem => (
                                <div className="d-flex gap-2 align-items-center mb-2" key={commandItem.type}>
                                  <Badge color="secondary" className="text-uppercase">
                                    {commandItem.type}
                                  </Badge>
                                  <Input
                                    readOnly
                                    bsSize="sm"
                                    className="font-monospace"
                                    value={commandItem.command}
                                  />
                                  <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    onClick={() => copyCommand(commandItem.command)}
                                    aria-label={`Copy ${commandItem.type} cron command`}
                                  >
                                    <i className="mdi mdi-content-copy" />
                                  </Button>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </TabPane>
      </TabContent>
    </PageWrapper>
  )
}

export default JobList
